import { AxiosInstance, AxiosResponse } from "axios";
import qs from "qs";
import ApiPath from "src/api/apiPath.json";
import { ApiResponse } from "../public-types";
import {
  BuildingAddModel,
  BuildingDetailResponseData,
  BuildingEditModel,
  BuildingListParams,
  BuildingListResponseData,
  BuildingDeskGroupListParams,
  BuildingDeskGroupModel,
  BuildingDeskGroupRequest,
  BuildingDeskListParams,
  BuildingDeskModel,
  BuildingDeskRequest,
  BuildingFacilityListParams,
  BuildingFacilityModel,
  BuildingFacilityRequest,
  BuildingFloorItem,
  BuildingFloorListParams,
  BuildingFloorRequest,
  BuildingRoomByBuildingItem,
  BuildingRoomItem,
  BuildingRoomListParams,
  BuildingRoomRequest,
  BuildingRoomsByBuildingParams,
  BuildingManageModel,
  BuildingStatus,
  BuildingUpdateResponseData,
  CommonFacilityListModel,
  CommonFacilityListParams,
} from "./building-types";

/* 
  RPB01. 건물 목록 조회
*/
export async function getBuildingsAsync(
  axios: AxiosInstance,
  params?: BuildingListParams,
): Promise<AxiosResponse<ApiResponse<BuildingListResponseData>>> {
  return await axios.get<ApiResponse<BuildingListResponseData>>(ApiPath.building.list, {
    params,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

/*
  CPB02. 건물 기본정보 상태 변경
*/
export async function changeBuildingStatusAsync(
  axios: AxiosInstance,
  params?: {
    buildingId: number;
    status: BuildingStatus;
  },
): Promise<AxiosResponse<ApiResponse<BuildingUpdateResponseData>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.status) {
    throw Error("status is not found.");
  }
  const path = ApiPath.building.status.replace("{id}", params.buildingId.toString());
  return await axios.patch(path, {
    building: { status: params.status },
  });
}

/*
  CPB03. 건물 기본 정보 등록
*/
export async function createBuildingAsync(
  axios: AxiosInstance,
  params?: {
    building: BuildingAddModel;
  },
): Promise<AxiosResponse<ApiResponse<BuildingUpdateResponseData>>> {
  return await axios.post(ApiPath.building.add, params);
}

/*
  CPB04. 건물 기본 정보 수정
*/
export async function updateBuildingAsync(
  axios: AxiosInstance,
  params?: {
    building: BuildingEditModel;
  },
): Promise<AxiosResponse<ApiResponse<BuildingUpdateResponseData>>> {
  if (!params?.building?.id) {
    throw Error("id is not found.");
  }
  const path = ApiPath.building.edit.replace("{id}", params.building.id.toString());
  return await axios.put(path, params);
}

/* 
  RPB05. 건물 정보 조회
*/
export async function getBuildingAsync(
  axios: AxiosInstance,
  params?: {
    buildingId: number;
  },
): Promise<AxiosResponse<ApiResponse<BuildingDetailResponseData>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const id = params.buildingId.toString();
  const path = ApiPath.building.detail.replace("{id}", id);
  const result = await axios.get(path);
  if (!result.data?.data?.content?.building) {
    throw Error(`건물 id: ${id} 의 건물 정보 조회를 실패하였습니다.`);
  }
  return result;
}

/* 
  CPB06. 건물 기본정보 삭제
*/
export async function deleteBuildingAsync(
  axios: AxiosInstance,
  params?: {
    buildingId: number;
  },
): Promise<AxiosResponse<ApiResponse<BuildingUpdateResponseData>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const path = ApiPath.building.delete.replace("{id}", params.buildingId.toString());
  return await axios.delete(path);
}

/* 
  RPB20. 건물 공용공간 상세 조회 목록
*/

export async function getCommonFacilityListAsync(
  axios: AxiosInstance,
  params?: CommonFacilityListParams,
): Promise<AxiosResponse<ApiResponse<{ content: CommonFacilityListModel[] }>>> {
  const path = ApiPath.building.commonfacilityList;
  return await axios.get<ApiResponse<{ content: CommonFacilityListModel[] }>>(path, {
    params,
    paramsSerializer: {
      serialize: (params) => {
        return qs.stringify(params, { allowDots: true, encode: true });
      },
    },
  });
}

/*
  RPBF01. 건물 회의실 목록 페이지 조회
  GET /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/meeting-rooms
*/
export async function getBuildingMeetingRoomsAsync(
  axios: AxiosInstance,
  params?: { buildingId: number } & BuildingFacilityListParams,
): Promise<AxiosResponse<ApiResponse<{ content: BuildingFacilityModel[] }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const { buildingId, ...queryParams } = params;
  const path = ApiPath.building.meetingRooms.replace("{buildingId}", buildingId.toString());
  return await axios.get<ApiResponse<{ content: BuildingFacilityModel[] }>>(path, {
    params: queryParams,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

/*
  RPBF01. 건물 편의시설 목록 페이지 조회
  GET /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/conveniences
*/
export async function getBuildingConveniencesAsync(
  axios: AxiosInstance,
  params?: { buildingId: number } & BuildingFacilityListParams,
): Promise<AxiosResponse<ApiResponse<{ content: BuildingFacilityModel[] }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const { buildingId, ...queryParams } = params;
  const path = ApiPath.building.conveniences.replace("{buildingId}", buildingId.toString());
  return await axios.get<ApiResponse<{ content: BuildingFacilityModel[] }>>(path, {
    params: queryParams,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

/*
  RPBF08. 건물 좌석 그룹 목록 페이지 조회
  GET /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/desk-groups
*/
export async function getBuildingDeskGroupsAsync(
  axios: AxiosInstance,
  params?: { buildingId: number } & BuildingDeskGroupListParams,
): Promise<AxiosResponse<ApiResponse<{ content: BuildingDeskGroupModel[] }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const { buildingId, ...queryParams } = params;
  const path = ApiPath.building.deskGroups.replace("{buildingId}", buildingId.toString());
  return await axios.get<ApiResponse<{ content: BuildingDeskGroupModel[] }>>(path, {
    params: queryParams,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

/*
  RPBF04. 건물 좌석 목록 페이지 조회
  GET /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/desks
*/
export async function getBuildingDesksAsync(
  axios: AxiosInstance,
  params?: { buildingId: number } & BuildingDeskListParams,
): Promise<AxiosResponse<ApiResponse<{ content: BuildingDeskModel[] }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const { buildingId, ...queryParams } = params;
  const path = ApiPath.building.desks.replace("{buildingId}", buildingId.toString());
  return await axios.get<ApiResponse<{ content: BuildingDeskModel[] }>>(path, {
    params: queryParams,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

/*
  RPBF02. 건물 회의실 등록 처리
  POST /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/meeting-rooms
*/
export async function postBuildingMeetingRoomAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; facility: BuildingFacilityRequest },
): Promise<AxiosResponse<ApiResponse<{ facility: BuildingFacilityModel }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const path = ApiPath.building.meetingRooms.replace("{buildingId}", params.buildingId.toString());
  return await axios.post<ApiResponse<{ facility: BuildingFacilityModel }>>(path, params.facility);
}

/*
  RPBF02. 건물 편의시설 등록 처리
  POST /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/conveniences
*/
export async function postBuildingConvenienceAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; facility: BuildingFacilityRequest },
): Promise<AxiosResponse<ApiResponse<{ facility: BuildingFacilityModel }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const path = ApiPath.building.conveniences.replace("{buildingId}", params.buildingId.toString());
  return await axios.post<ApiResponse<{ facility: BuildingFacilityModel }>>(path, params.facility);
}

/*
  RPBF03. 건물 회의실 수정 처리
  PATCH /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/meeting-rooms/{facilityId}
*/
export async function patchBuildingMeetingRoomAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; facilityId: number; facility: BuildingFacilityRequest },
): Promise<AxiosResponse<ApiResponse<{ facility: BuildingFacilityModel }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.facilityId) {
    throw Error("facilityId is not found.");
  }
  const path = ApiPath.building.meetingRoomDetail
    .replace("{buildingId}", params.buildingId.toString())
    .replace("{facilityId}", params.facilityId.toString());
  return await axios.patch<ApiResponse<{ facility: BuildingFacilityModel }>>(path, params.facility);
}

/*
  RPBF03. 건물 편의시설 수정 처리
  PATCH /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/conveniences/{facilityId}
*/
export async function patchBuildingConvenienceAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; facilityId: number; facility: BuildingFacilityRequest },
): Promise<AxiosResponse<ApiResponse<{ facility: BuildingFacilityModel }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.facilityId) {
    throw Error("facilityId is not found.");
  }
  const path = ApiPath.building.convenienceDetail
    .replace("{buildingId}", params.buildingId.toString())
    .replace("{facilityId}", params.facilityId.toString());
  return await axios.patch<ApiResponse<{ facility: BuildingFacilityModel }>>(path, params.facility);
}

/*
  RPBF07. 건물 공용 공간 삭제 처리 (회의실/편의시설/좌석 공통)
  DELETE /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/facilities/{facilityId}
*/
export async function deleteBuildingFacilityAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; facilityId: number },
): Promise<AxiosResponse<ApiResponse<unknown>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.facilityId) {
    throw Error("facilityId is not found.");
  }
  const path = ApiPath.building.facilityDelete
    .replace("{buildingId}", params.buildingId.toString())
    .replace("{facilityId}", params.facilityId.toString());
  return await axios.delete<ApiResponse<unknown>>(path);
}

/*
  RPBF05. 건물 좌석 등록 처리
  POST /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/desks
*/
export async function postBuildingDeskAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; desk: BuildingDeskRequest },
): Promise<AxiosResponse<ApiResponse<{ desk: BuildingDeskModel }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const path = ApiPath.building.desks.replace("{buildingId}", params.buildingId.toString());
  return await axios.post<ApiResponse<{ desk: BuildingDeskModel }>>(path, params.desk);
}

/*
  RPBF06. 건물 좌석 수정 처리
  PATCH /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/desks/{facilityId}
*/
export async function patchBuildingDeskAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; facilityId: number; desk: BuildingDeskRequest },
): Promise<AxiosResponse<ApiResponse<{ desk: BuildingDeskModel }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.facilityId) {
    throw Error("facilityId is not found.");
  }
  const path = ApiPath.building.deskDetail
    .replace("{buildingId}", params.buildingId.toString())
    .replace("{facilityId}", params.facilityId.toString());
  return await axios.patch<ApiResponse<{ desk: BuildingDeskModel }>>(path, params.desk);
}

/*
  RPBF09. 건물 좌석 그룹 등록 처리
  POST /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/desk-groups
*/
export async function postBuildingDeskGroupAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; deskGroup: BuildingDeskGroupRequest },
): Promise<AxiosResponse<ApiResponse<{ deskGroup: BuildingDeskGroupModel }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const path = ApiPath.building.deskGroups.replace("{buildingId}", params.buildingId.toString());
  return await axios.post<ApiResponse<{ deskGroup: BuildingDeskGroupModel }>>(path, params.deskGroup);
}

/*
  RPBF10. 건물 좌석 그룹 수정 처리
  PATCH /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/desk-groups/{deskGroupId}
*/
export async function patchBuildingDeskGroupAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; deskGroupId: number; deskGroup: BuildingDeskGroupRequest },
): Promise<AxiosResponse<ApiResponse<{ deskGroup: BuildingDeskGroupModel }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.deskGroupId) {
    throw Error("deskGroupId is not found.");
  }
  const path = ApiPath.building.deskGroupDetail
    .replace("{buildingId}", params.buildingId.toString())
    .replace("{deskGroupId}", params.deskGroupId.toString());
  return await axios.patch<ApiResponse<{ deskGroup: BuildingDeskGroupModel }>>(path, params.deskGroup);
}

/*
  RPBF11. 건물 좌석 그룹 삭제 처리
  DELETE /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/desk-groups/{deskGroupId}
*/
export async function deleteBuildingDeskGroupAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; deskGroupId: number },
): Promise<AxiosResponse<ApiResponse<unknown>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.deskGroupId) {
    throw Error("deskGroupId is not found.");
  }
  const path = ApiPath.building.deskGroupDetail
    .replace("{buildingId}", params.buildingId.toString())
    .replace("{deskGroupId}", params.deskGroupId.toString());
  return await axios.delete<ApiResponse<unknown>>(path);
}

/*
  RPBP01. 건물 층 목록 페이지 조회
  GET /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/floors
*/
export async function getBuildingFloorsAsync(
  axios: AxiosInstance,
  params?: { buildingId: number } & BuildingFloorListParams,
): Promise<AxiosResponse<ApiResponse<{ content: BuildingFloorItem[] }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const { buildingId, ...queryParams } = params;
  const path = ApiPath.building.floors.replace("{buildingId}", buildingId.toString());
  return await axios.get<ApiResponse<{ content: BuildingFloorItem[] }>>(path, {
    params: queryParams,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

/*
  RPBP02. 건물 층 등록 처리
  POST /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/floors
*/
export async function postBuildingFloorAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; floor: BuildingFloorRequest },
): Promise<AxiosResponse<ApiResponse<{ floor: BuildingFloorItem }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const path = ApiPath.building.floors.replace("{buildingId}", params.buildingId.toString());
  return await axios.post<ApiResponse<{ floor: BuildingFloorItem }>>(path, params.floor);
}

/*
  RPBP03. 건물 층 수정 처리
  PATCH /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/floors/{floorId}
*/
export async function patchBuildingFloorAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; floorId: number; floor: BuildingFloorRequest },
): Promise<AxiosResponse<ApiResponse<{ floor: BuildingFloorItem }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.floorId) {
    throw Error("floorId is not found.");
  }
  const path = ApiPath.building.floorDetail
    .replace("{buildingId}", params.buildingId.toString())
    .replace("{floorId}", params.floorId.toString());
  return await axios.patch<ApiResponse<{ floor: BuildingFloorItem }>>(path, params.floor);
}

/*
  RPBP04. 건물 층 삭제 처리
  DELETE /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/floors/{floorId}
*/
export async function deleteBuildingFloorAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; floorId: number },
): Promise<AxiosResponse<ApiResponse<unknown>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.floorId) {
    throw Error("floorId is not found.");
  }
  const path = ApiPath.building.floorDetail
    .replace("{buildingId}", params.buildingId.toString())
    .replace("{floorId}", params.floorId.toString());
  return await axios.delete<ApiResponse<unknown>>(path);
}

/*
  RPBP05. 건물 호 목록 페이지 조회
  GET /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/floors/{floorId}/rooms
*/
export async function getBuildingRoomsAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; floorId: number } & BuildingRoomListParams,
): Promise<AxiosResponse<ApiResponse<{ content: BuildingRoomItem[] }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.floorId) {
    throw Error("floorId is not found.");
  }
  const { buildingId, floorId, ...queryParams } = params;
  const path = ApiPath.building.rooms
    .replace("{buildingId}", buildingId.toString())
    .replace("{floorId}", floorId.toString());
  return await axios.get<ApiResponse<{ content: BuildingRoomItem[] }>>(path, {
    params: queryParams,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

/*
  RPBP09. 건물 호 목록 페이지 조회 (건물 단위)
  GET /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/rooms
*/
export async function getBuildingRoomsByBuildingAsync(
  axios: AxiosInstance,
  params?: { buildingId: number } & BuildingRoomsByBuildingParams,
): Promise<AxiosResponse<ApiResponse<{ content: BuildingRoomByBuildingItem[] }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  const { buildingId, ...queryParams } = params;
  const path = ApiPath.building.buildingRooms.replace("{buildingId}", buildingId.toString());
  return await axios.get<ApiResponse<{ content: BuildingRoomByBuildingItem[] }>>(path, {
    params: queryParams,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

/*
  RPBP06. 건물 호 등록 처리
  POST /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/floors/{floorId}/rooms
*/
export async function postBuildingRoomAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; floorId: number; room: BuildingRoomRequest },
): Promise<AxiosResponse<ApiResponse<{ room: BuildingRoomItem }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.floorId) {
    throw Error("floorId is not found.");
  }
  const path = ApiPath.building.rooms
    .replace("{buildingId}", params.buildingId.toString())
    .replace("{floorId}", params.floorId.toString());
  return await axios.post<ApiResponse<{ room: BuildingRoomItem }>>(path, params.room);
}

/*
  RPBP07. 건물 호 수정 처리
  PATCH /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/floors/{floorId}/rooms/{roomId}
*/
export async function patchBuildingRoomAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; floorId: number; roomId: number; room: BuildingRoomRequest },
): Promise<AxiosResponse<ApiResponse<{ room: BuildingRoomItem }>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.floorId) {
    throw Error("floorId is not found.");
  }
  if (!params?.roomId) {
    throw Error("roomId is not found.");
  }
  const path = ApiPath.building.roomDetail
    .replace("{buildingId}", params.buildingId.toString())
    .replace("{floorId}", params.floorId.toString())
    .replace("{roomId}", params.roomId.toString());
  return await axios.patch<ApiResponse<{ room: BuildingRoomItem }>>(path, params.room);
}

/*
  RPBP08. 건물 호 삭제 처리
  DELETE /api/ctrl/partners/{partnerId}/pr/buildings/{buildingId}/floors/{floorId}/rooms/{roomId}
*/
export async function deleteBuildingRoomAsync(
  axios: AxiosInstance,
  params?: { buildingId: number; floorId: number; roomId: number },
): Promise<AxiosResponse<ApiResponse<unknown>>> {
  if (!params?.buildingId) {
    throw Error("buildingId is not found.");
  }
  if (!params?.floorId) {
    throw Error("floorId is not found.");
  }
  if (!params?.roomId) {
    throw Error("roomId is not found.");
  }
  const path = ApiPath.building.roomDetail
    .replace("{buildingId}", params.buildingId.toString())
    .replace("{floorId}", params.floorId.toString())
    .replace("{roomId}", params.roomId.toString());
  return await axios.delete<ApiResponse<unknown>>(path);
}

/**RCN20.건물 운영정보 조회 */
export async function getManageBuildingAsync(
  axios: AxiosInstance,
  params?: { contractManageId: string },
): Promise<AxiosResponse<ApiResponse<{ buildingManageList: BuildingManageModel[] }>>> {
  const path = ApiPath.contractBuilding.manageBuilding.replace("{contractManageId}", String(params?.contractManageId));
  return await axios.get(path, {
    params,
  });
}
